/**
 * canvas-engine/presets/presetData/waasPresets.js
 *
 * TRANSCRIBED WAAS (IT services) default look-and-feel VALUES (data only).
 *
 * ZERO-REUSE CONSTRAINT (Task 3.1): imports NOTHING. Values transcribed from
 * the Default_Source (`waasTemplateDefaults.js` + `ConsoleWaasPreview`) as
 * plain DATA. No React, no rendering, no side effects.
 *
 * Consumed by the own decomposition adapters (Task 3.2/3.3) to lay out editable
 * Canvas_Elements at the fixed Reference_Width (1120px). See educationPresets.js
 * for the shared descriptor shape.
 *
 * Requirements: 3.6, 3.7, 5.1, 5.3, 5.6, 5.7
 */

/** Default imagery (transcribed from `waasImages`). */
export const waasImages = {
  HERO: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=85&fit=crop&auto=format',
  STORY: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1200&q=80&fit=crop&auto=format',
  SERVICE: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=1200&q=80&fit=crop&auto=format',
  SERVER: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1200&q=85&fit=crop&auto=format',
  CAREER: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1200&q=80&fit=crop&auto=format',
  AI_INSIGHTS: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&q=80&fit=crop&auto=format',
  GALLERY_ONE: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&q=80&fit=crop&auto=format',
  GALLERY_TWO: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&q=80&fit=crop&auto=format',
  GALLERY_THREE: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&q=80&fit=crop&auto=format',
};

/** Theme-level style values transcribed from WAAS `themeDefaults`. */
export const waasTheme = {
  primary: '#07111f',
  secondary: '#d8c2b6',
  accent: '#0b0f16',
  background: '#e6e8eb',
  surface: '#ffffff',
  color: '#0b0f16',
  mutedColor: '#5f6670',
  borderColor: '#d5d8dd',
  baseFontSize: '14px',
  headingWeight: '300',
  bodyWeight: '400',
  lineHeight: '1.55',
  pageWidth: '1120px',
  sectionSpacing: '72px 24px',
  gridGap: '16px',
  buttonRadius: '999px',
  cardRadius: '24px',
  cardShadow: '0 18px 42px rgba(24, 34, 46, 0.10)',
  imageRadius: '24px',
  headerHeight: '48px',
};

// ── Local pure data helpers ──
const img = (url = '') => ({ storedLink: '', displayLink: url });
const action = (label, url, variant = 'primary') => ({ label, url, variant, visible: true });
const block = (title, description = '', icon = '', extra = {}) => ({
  title,
  description,
  icon,
  visible: true,
  ...extra,
});

// Frame style base mirrors the Default_Source `baseStyle` (scaled padding ~0.8x
// is the adapters' concern; we keep the authored padding values here).
const style = (overrides = {}) => ({
  background: '#f8fafc',
  textColor: '#030303',
  accentColor: '#23196E',
  secondaryColor: '#e8f3ff',
  padding: '96px 24px',
  ...overrides,
});

/**
 * WAAS_PRESETS — per-section-type default descriptors.
 * Full WAAS section list covered (hero, trustedLogos, aboutStory, philosophy,
 * whyAvps, servicesGrid, serviceDetail, statsStrip, processSteps,
 * instructorSpotlight, schedule, pricingPlans, testimonials, faq, imageGallery,
 * careerHero, openRoles, contact, auth, newsletterFooter).
 */
export const WAAS_PRESETS = {
  waasHero: {
    sectionType: 'waasHero',
    label: 'WaaS IT hero',
    domain: 'waas',
    style: style({ background: '#2f6f8f', padding: '0', minHeight: '640px' }),
    layout: { align: 'left', width: 'full', columns: 1 },
    content: {
      eyebrow: 'Cloud, software, and managed IT',
      heading: 'Designing digital systems that shape operations',
      accentWord: 'systems',
      subheading:
        'Architecture, cloud, server operations, and software delivery shaped into calm digital experiences.',
      badgeLabel: 'AVPS Tech',
      statValue: '24/7',
      statLabel: 'infrastructure support, deployment guidance, and platform monitoring',
      images: [img(waasImages.HERO)],
      imageAlt: 'Technology team building modern software systems',
      actions: [
        action('Explore services', '/services', 'primary'),
        action('Server infrastructure', '/server', 'outline'),
      ],
    },
  },

  waasTrustedLogos: {
    sectionType: 'waasTrustedLogos',
    label: 'Trusted logos',
    domain: 'waas',
    style: style({ background: '#ffffff', padding: '56px 24px', border: '1px solid #e2e1d9' }),
    layout: { align: 'left', width: 'full' },
    content: {
      eyebrow: 'CORE CAPABILITIES',
      blocks: [
        block('Cloud Platforms'),
        block('Server Operations'),
        block('Software Delivery'),
        block('Automation'),
      ],
    },
  },

  waasAboutStory: {
    sectionType: 'waasAboutStory',
    label: 'Story split',
    domain: 'waas',
    style: style({ background: '#e6e8eb', padding: '68px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      eyebrow: 'ABOUT AVPS TECH',
      heading: 'Systems that speak',
      subheading:
        'Our work is rooted in the business experience, crafting systems that enable connection, invite interaction, and leave lasting operational impressions.',
      body:
        'From application modernization to server operations, every layer is planned to feel clear, secure, and practical.',
      images: [img(waasImages.STORY)],
      imageAlt: 'Technology team planning a digital platform',
      actions: [action('ABOUT US', '/about', 'primary')],
      blocks: [
        block('Contemporary Visioning', 'Creating modern technology spaces that feel warm and personal.'),
        block('Reliable System Balance', 'Combining vision and detail to perfect every production layer.'),
        block('Customized Delivery', 'Creating platforms that reflect each client workflow.', '', { imageContent: img(waasImages.SERVICE) }),
        block('Elegant IT Operations', 'Transforming support into daily confidence.', '', { imageContent: img(waasImages.SERVER) }),
      ],
    },
  },

  waasPhilosophy: {
    sectionType: 'waasPhilosophy',
    label: 'Philosophy quote',
    domain: 'waas',
    style: style({ background: '#e6e8eb', padding: '72px 24px' }),
    layout: { align: 'center', width: 'full' },
    content: {
      heading: 'Beyond the server room',
      quote:
        'Good technology should feel almost invisible: dependable, observable, secure, and ready for the next idea.',
      author: 'AVPS TECH',
    },
  },

  waasWhyAvps: {
    sectionType: 'waasWhyAvps',
    label: 'Why choose us',
    domain: 'waas',
    style: style({ background: '#e6e8eb', padding: '72px 24px' }),
    layout: { align: 'center', width: 'full', columns: 4 },
    content: {
      heading: 'Why teams build with us',
      subheading: '',
      blocks: [
        block('Architecture-first delivery', 'Cloud and software decisions start with foundations that can scale.', '01'),
        block('Server confidence', 'Operating systems, deployment paths, monitoring, and support stay visible.', '02'),
        block('Automation clarity', 'Business workflows become easier to run, audit, and improve.', '03'),
        block('Production mindset', 'Launch plans include security, maintenance, and long-term ownership.', '04'),
      ],
    },
  },

  waasServicesGrid: {
    sectionType: 'waasServicesGrid',
    label: 'Services grid',
    domain: 'waas',
    style: style({ background: '#e6e8eb', padding: '72px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 3 },
    content: {
      eyebrow: 'SELECTED SERVICES',
      heading: 'Selected Services',
      subheading:
        'A focused service stack for organizations that need dependable platforms, connected workflows, and infrastructure that can keep up.',
      metrics: [
        { label: 'Platform', value: 'Cloud' },
        { label: 'Delivery', value: '2026' },
        { label: 'Client', value: 'Teams' },
      ],
      cards: [
        block('Platform Design', 'A calm foundation for portals, workflows, APIs, and business operations.', '01', { badge: 'Cloud UI', imageContent: img(waasImages.SERVICE), buttonLabel: 'Read more', url: '/services' }),
        block('Server Estate', 'Hybrid and cloud infrastructure planned around uptime, security, and ownership.', '02', { badge: 'Infra', imageContent: img(waasImages.SERVER), buttonLabel: 'View detail', url: '/server' }),
        block('Automation Flow', 'Connected approvals, alerts, reporting, and operational routines.', '03', { badge: 'Workflow', imageContent: img(waasImages.AI_INSIGHTS), buttonLabel: 'Read more', url: '/services' }),
        block('Data Layer', 'Dashboards, integrations, and decision support that teams can trust.', '04', { badge: 'Data', imageContent: img(waasImages.GALLERY_ONE), buttonLabel: 'Read more', url: '/services' }),
        block('Cloud Horizon', 'Migration and modernization paths for applications that need room to grow.', '05', { badge: 'Cloud', imageContent: img(waasImages.GALLERY_TWO), buttonLabel: 'Read more', url: '/services' }),
        block('Support Estate', 'Managed service practices for release support, monitoring, and maintenance.', '06', { badge: 'Support', imageContent: img(waasImages.CAREER), buttonLabel: 'Read more', url: '/contactus' }),
      ],
    },
  },

  waasServiceDetail: {
    sectionType: 'waasServiceDetail',
    label: 'Service detail',
    domain: 'waas',
    style: style({ background: '#eef2f4', padding: '84px 24px', accentColor: '#c9a46a' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      anchorId: 'server-infrastructure',
      eyebrow: 'SERVER & CLOUD INFRASTRUCTURE',
      heading: 'Server and cloud infrastructure built for scale.',
      subheading:
        'Plan, migrate, secure, and operate the infrastructure layer behind high-availability products, internal platforms, and client-facing portals.',
      images: [img(waasImages.SERVER)],
      visualTitle: 'Production-grade cloud estate',
      visualSubtitle: 'Network, compute, storage, observability, and release controls aligned before workloads move.',
      actions: [action('Talk infrastructure', '/contactus', 'primary')],
      metrics: [
        { value: '99.9%', label: 'uptime planning', visible: true },
        { value: 'Zero Trust', label: 'security posture', visible: true },
        { value: '24/7', label: 'operational visibility', visible: true },
      ],
      cards: [
        block('Cloud Readiness', 'Workload mapping, cost posture, migration risks, and target environments clarified before implementation.', '01'),
        block('Secure Migration', 'Staged runbooks for databases, applications, DNS, access, backups, and rollback planning.', '02'),
        block('Managed Operations', 'Monitoring, patching, incident response, ownership models, and support routines designed for continuity.', '03'),
        block('Release Systems', 'CI/CD, deployment gates, environment strategy, and observability connected into one delivery flow.', '04'),
      ],
    },
  },

  waasStatsStrip: {
    sectionType: 'waasStatsStrip',
    label: 'Stats strip',
    domain: 'waas',
    style: style({ background: '#0b0f16', textColor: '#ffffff', padding: '78px 30px' }),
    layout: { align: 'center', width: 'contained', columns: 4 },
    content: {
      eyebrow: 'DELIVERY SIGNALS',
      heading: 'Practical execution with infrastructure discipline.',
      subheading:
        'Use measurable proof points to show how your team builds and supports critical technology systems.',
      stats: [
        { value: '99%', label: 'operational visibility', visible: true },
        { value: '24/7', label: 'support-ready architecture', visible: true },
        { value: '50+', label: 'automation workflows', visible: true },
        { value: '15+', label: 'years of IT delivery', visible: true },
      ],
    },
  },

  waasProcessSteps: {
    sectionType: 'waasProcessSteps',
    label: 'Process steps',
    domain: 'waas',
    style: style({ background: '#e6e8eb', padding: '72px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 3 },
    content: {
      eyebrow: 'HOW IT WORKS',
      heading: 'A clear path from idea to operation.',
      subheading:
        'Every engagement moves through discovery, architecture, delivery, and support without losing sight of business outcomes.',
      blocks: [
        block('Discover', 'Map goals, users, systems, servers, risks, and business priorities.', '01', { badge: 'Step one' }),
        block('Design', 'Shape architecture, delivery milestones, security, and infrastructure choices.', '02', { badge: 'Step two' }),
        block('Operate', 'Ship, monitor, support, and improve the platform after launch.', '03', { badge: 'Step three' }),
      ],
    },
  },

  waasInstructorSpotlight: {
    sectionType: 'waasInstructorSpotlight',
    label: 'Expertise spotlight',
    domain: 'waas',
    style: style({ background: '#e6e8eb', padding: '72px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      eyebrow: 'TECHNICAL EXPERTISE',
      heading: 'Cloud, DevOps, data, and software engineering in one delivery motion.',
      subheading:
        'Use this section to list your strongest platforms, frameworks, cloud providers, DevOps tooling, programming languages, and data capabilities.',
      images: [img(waasImages.AI_INSIGHTS)],
      imageAlt: 'AI and predictive technology illustration',
      actions: [action('Learn about us', '/about', 'primary')],
      blocks: [
        block('Platform expertise', 'Add your strongest enterprise platforms, cloud services, and integration tools.', '01'),
        block('Cloud-native delivery', 'Highlight container platforms, cloud services, data stores, and deployment models.', '02'),
        block('Engineering tooling', 'List your DevOps, CI/CD, automation, testing, and software development tools.', '03'),
      ],
    },
  },

  waasSchedule: {
    sectionType: 'waasSchedule',
    label: 'Consulting flow',
    domain: 'waas',
    style: style({ background: '#f4f5f6', padding: '68px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 1 },
    content: {
      eyebrow: 'START A PROJECT',
      heading: 'Start with a focused technology conversation.',
      subheading:
        'Clarify your software, automation, cloud migration, server operations, or managed support needs.',
      timezoneLabel: 'Remote and on-site assistance available',
      actions: [action('Contact us', '/contactus', 'primary')],
      items: [
        { title: 'Consultation session', description: 'Identify business rules, technical state, and project goals.', date: '01', time: 'Plan', badge: 'Consult', buttonLabel: 'Start', url: '/contactus', visible: true },
        { title: 'Solution roadmap', description: 'Define implementation, modernization, migration, or managed-service steps.', date: '02', time: 'Design', badge: 'Roadmap', buttonLabel: 'Discuss', url: '/contactus', visible: true },
        { title: 'Delivery support', description: 'Move from architecture and development into deployment, maintenance, and enablement.', date: '03', time: 'Build', badge: 'Deliver', buttonLabel: 'Connect', url: '/contactus', visible: true },
      ],
    },
  },

  waasPricingPlans: {
    sectionType: 'waasPricingPlans',
    label: 'Solution packages',
    domain: 'waas',
    style: style({ background: '#e6e8eb', padding: '72px 24px' }),
    layout: { align: 'center', width: 'contained', columns: 3 },
    content: {
      eyebrow: 'ENGAGEMENTS',
      heading: 'Service packages for modern IT teams.',
      subheading:
        'Start with discovery, deliver a defined project, or keep systems healthy through ongoing support.',
      disclaimer: 'Replace these examples with your own offers, pricing model, or consultation paths.',
      cards: [
        block('Automation Package', 'Business process automation, rule-driven workflows, and implementation support.', '01', { price: 'Plan', period: 'automation', features: ['Workflow discovery', 'Rules and approvals', 'Business user enablement'], buttonLabel: 'Learn more', url: '/services' }),
        block('Server & Cloud Package', 'Cloud migration, containerization, infrastructure planning, and deployment support.', '02', { price: 'Plan', period: 'infrastructure', badge: 'Server', features: ['Cloud readiness review', 'Secure migration path', 'Deployment best practices'], buttonLabel: 'Explore', url: '/services#server-infrastructure', backgroundColor: '#f4f5f6' }),
        block('Managed IT Package', 'Ongoing application support, monitoring, maintenance, and delivery assistance.', '03', { price: 'Plan', period: 'support', features: ['Application maintenance', 'Issue triage', 'Continuous improvement'], buttonLabel: 'Contact us', url: '/contactus' }),
      ],
    },
  },

  waasTestimonials: {
    sectionType: 'waasTestimonials',
    label: 'Testimonials',
    domain: 'waas',
    style: style({ background: '#e6e8eb', padding: '78px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 3 },
    content: {
      eyebrow: 'CLIENT VOICES',
      heading: 'Voices from those we have built with.',
      subheading:
        'Our mixed technology team handled complex needs, balanced bold architecture with real-world constraints, and delivered an efficient final platform.',
      cards: [
        block('Jean Sricelle', 'Platform Director', '', { badge: 'Cloud', quote: 'Visionary systems design with practical execution.', imageContent: img(waasImages.STORY) }),
        block('Jordan Mills', 'CTO', '', { badge: 'Servers', quote: 'Infrastructure choices became clearer and easier to trust.', imageContent: img(waasImages.CAREER) }),
        block('Mira Shah', 'Operations Lead', '', { badge: 'Automation', quote: 'The team turned scattered workflows into a platform people use.', imageContent: img(waasImages.GALLERY_THREE) }),
        block('Alex Chen', 'Founder', '', { badge: 'Data', quote: 'Delivery stayed practical from concept through launch.', imageContent: img(waasImages.AI_INSIGHTS) }),
        block('Sam Rivera', 'Product Lead', '', { badge: 'Support', quote: 'Support and monitoring finally felt connected.', imageContent: img(waasImages.SERVER) }),
      ],
    },
  },

  waasFaq: {
    sectionType: 'waasFaq',
    label: 'FAQ accordion',
    domain: 'waas',
    style: style({ background: '#e6e8eb', padding: '68px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      eyebrow: 'QUESTIONS',
      heading: 'Common questions before working with your team.',
      subheading:
        'Use these defaults for services, careers, product implementation, contact, or onboarding content.',
      items: [
        block('Can your team join an in-progress project?', 'Yes. Use this answer to explain how your team supports discovery, development, QA, production, and maintenance stages.'),
        block('Do you provide managed IT services?', 'Yes. Describe your application support, monitoring, maintenance, and infrastructure coverage here.'),
        block('Do you help with cloud migration?', 'Yes. Explain your approach for cloud readiness, architecture, migration planning, and deployment.'),
        block('Can clients customize this engagement?', 'Yes. Use this answer to clarify consultation, package, or service customization options.'),
      ],
    },
  },

  waasImageGallery: {
    sectionType: 'waasImageGallery',
    label: 'Image gallery',
    domain: 'waas',
    style: style({ background: '#e6e8eb', padding: '72px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 3 },
    content: {
      eyebrow: 'BEYOND THE BLUEPRINT',
      heading: 'Beyond the Blueprint',
      subheading:
        'Exploring ideas, patterns, and innovations that shape the systems teams run every day.',
      images: [img(waasImages.GALLERY_TWO), img(waasImages.GALLERY_ONE), img(waasImages.GALLERY_THREE)],
      cards: [
        block('Designing Systems That Inspire', 'Technology is more than servers and code. It is about shaping experiences as businesses evolve and operating models change.', '', { imageContent: img(waasImages.GALLERY_TWO), badge: 'Article' }),
        block('Cloud Computing', 'Deploying platforms onto the cloud.', '', { imageContent: img(waasImages.GALLERY_ONE), badge: 'Cloud' }),
        block('DevOps Tooling', 'Developing and supporting enterprise applications using DevOps tools.', '', { imageContent: img(waasImages.GALLERY_THREE), badge: 'DevOps' }),
      ],
    },
  },

  waasCareerHero: {
    sectionType: 'waasCareerHero',
    label: 'Career hero',
    domain: 'waas',
    style: style({ background: '#e6e8eb', padding: '72px 24px' }),
    layout: { align: 'center', width: 'contained', columns: 2 },
    content: {
      eyebrow: 'CAREERS',
      heading: 'Build useful technology with us.',
      subheading:
        'Join teams working across software, cloud, server operations, automation, and customer success.',
      images: [img(waasImages.CAREER)],
      actions: [
        action('See open roles', '/careers', 'primary'),
        action('Email us', '/contactus', 'outline'),
      ],
    },
  },

  waasOpenRoles: {
    sectionType: 'waasOpenRoles',
    label: 'Open roles',
    domain: 'waas',
    style: style({ background: '#e6e8eb', padding: '68px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 3 },
    content: {
      eyebrow: 'OPEN ROLES',
      heading: 'Current openings',
      subheading:
        'Use these cards for current openings, role expectations, locations, and application instructions.',
      cards: [
        block('Full Stack Developer', 'Develop, validate, automate, and create quality solutions focused on client business needs using modern frameworks, CI/CD, and cloud-aware engineering practices.', '', { badge: 'Full Time', location: 'Add application details' }),
        block('Software Engineer', 'Develop, customize, and implement enterprise applications using your preferred languages, platforms, cloud services, and delivery tooling.', '', { badge: 'Full Time', location: 'Add location' }),
      ],
    },
  },

  waasContact: {
    sectionType: 'waasContact',
    label: 'Contact page',
    domain: 'waas',
    style: style({ background: '#e6e8eb', padding: '72px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      eyebrow: 'CONTACT US',
      heading: 'Let us build your vision together.',
      subheading:
        'Share your software, cloud, server, or automation goals and we will shape the next practical step.',
      phoneTitle: 'Phone',
      phoneValue: '+1 (555) 400-1020',
      phoneHours: 'Business hours',
      emailTitle: 'Email',
      emailValue: 'hello@avpstech.com',
      addressTitle: 'Office',
      addressValue: 'Add your office address\nCity, State ZIP\nCountry',
      formTitle: 'Get in Touch',
      submitLabel: 'SUBMIT MESSAGE',
      socialLinks: [
        { label: 'LinkedIn', route: '', icon: 'Linkedin', platformKey: 'linkedin', visible: true },
        { label: 'YouTube', route: '', icon: 'Youtube', platformKey: 'youtube', visible: true },
        { label: 'Facebook', route: '', icon: 'Facebook', platformKey: 'facebook', visible: true },
        { label: 'X', route: '', icon: 'Twitter', platformKey: 'twitter', visible: true },
      ],
      fields: [
        { label: 'Name *', type: 'text', required: true, visible: true },
        { label: 'Company', type: 'text', required: false, visible: true },
        { label: 'Email *', type: 'email', required: true, visible: true },
        { label: 'Phone Number', type: 'tel', required: false, visible: true },
        { label: 'Message *', type: 'textarea', required: true, visible: true },
      ],
    },
  },

  waasAuth: {
    sectionType: 'waasAuth',
    label: 'Auth page',
    domain: 'waas',
    style: style({ background: '#f8fafc', padding: '78px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      // Default reflects the sign-in mode (authSection default). Sign-up copy
      // noted inline for the adapter to swap when mode === 'signUp'.
      mode: 'signIn',
      eyebrow: 'CLIENT PORTAL',
      heading: 'Sign in to your client portal.',
      subheading:
        'Sign in to access project updates, service requests, onboarding details, and account information.',
      formTitle: 'Sign in',
      submitLabel: 'SIGN IN',
      alternateLabel: 'Need an account? Sign up',
      alternateUrl: '/sign-up',
      images: [img(waasImages.SERVER)],
      imageAlt: 'Technology services client portal',
      fields: [
        { label: 'Email', type: 'email', required: true, visible: true },
        { label: 'Password', type: 'password', required: true, visible: true },
      ],
      blocks: [
        block('Cloud implementation support', 'Keep service conversations connected to your delivery team.', '01'),
        block('Managed IT service access', 'Use one account entry point for support, onboarding, and follow-up.', '02'),
      ],
      // Sign-up variant (authSection mode === 'signUp'). The default content
      // above stays the sign-in form the library inserts; this `variants.signUp`
      // object carries the faithfully-transcribed sign-up copy + fields so a
      // sign-up section can be authored. It is preserved losslessly under
      // canvas.source.preserved.content and ignored by the default stack layout.
      variants: {
        signUp: {
          mode: 'signUp',
          eyebrow: 'CREATE YOUR ACCOUNT',
          heading: 'Create your technology account.',
          subheading:
            'Create an account to keep service conversations, onboarding details, and engagement updates in one place.',
          formTitle: 'Create account',
          submitLabel: 'CREATE ACCOUNT',
          alternateLabel: 'Already have an account? Sign in',
          alternateUrl: '/sign-in',
          images: [img(waasImages.GALLERY_THREE)],
          imageAlt: 'Technology deployment and onboarding',
          fields: [
            { label: 'First Name', type: 'text', required: true, visible: true },
            { label: 'Last Name', type: 'text', required: true, visible: true },
            { label: 'Email', type: 'email', required: true, visible: true },
            { label: 'Phone Number', type: 'tel', required: false, visible: true },
            { label: 'Password', type: 'password', required: true, visible: true },
          ],
        },
      },
    },
  },

  waasNewsletterFooter: {
    sectionType: 'waasNewsletterFooter',
    label: 'Newsletter band',
    domain: 'waas',
    style: style({ background: '#f4f5f6', padding: '56px 24px' }),
    layout: { align: 'left', width: 'contained' },
    content: {
      heading: 'Let us Build Your Vision Together',
      subheading:
        'From concept to creation, our team is ready to design systems that support your next move.',
      placeholder: 'work@company.com',
      buttonLabel: 'START YOUR PROJECT',
      buttonUrl: '/contactus',
      images: [img(waasImages.HERO)],
      consentLabel: 'YES, CONTACT ME ABOUT IT SERVICES.',
    },
  },
};

/** Section presets list (transcribed from `waasSectionPresets`). */
export const waasSectionPresets = [
  { type: 'customHtml', title: 'Custom section', description: 'Start blank with sanitized HTML and scoped CSS.', highlight: true },
  { type: 'waasHero', title: 'IT hero', description: 'Hero with image, stat, and CTAs.' },
  { type: 'waasAboutStory', title: 'Story split', description: 'Arched image and story copy.' },
  { type: 'waasPhilosophy', title: 'Philosophy', description: 'Sage quote section.' },
  { type: 'waasWhyAvps', title: 'Why choose us', description: 'Benefit icons and labels.' },
  { type: 'waasServicesGrid', title: 'Services', description: 'Service cards.' },
  { type: 'waasStatsStrip', title: 'Stats strip', description: 'Metrics in a quiet editorial band.' },
  { type: 'waasProcessSteps', title: 'Process', description: 'How it works cards.' },
  { type: 'waasInstructorSpotlight', title: 'Expertise', description: 'Technical expertise spotlight with image.' },
  { type: 'waasSchedule', title: 'Consulting flow', description: 'Discovery and delivery rows.' },
  { type: 'waasPricingPlans', title: 'Packages', description: 'Service package cards.' },
  { type: 'waasTestimonials', title: 'Stories', description: 'Client outcome cards.' },
  { type: 'waasFaq', title: 'FAQ', description: 'Question and answer list.' },
  { type: 'waasImageGallery', title: 'Gallery', description: 'Image tiles and captions.' },
  { type: 'waasContact', title: 'Contact', description: 'Contact details and form.' },
  { type: 'waasAuth', title: 'Sign in / Sign up', description: 'Account access form section.' },
];

export default WAAS_PRESETS;
